import { FaPlay } from 'react-icons/fa';

import defaultImage from 'assets/images/banner.jpeg';

import color from 'atoms/colors/colors';

// Quarks
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import ComponentButton from 'components/Button/ComponentButton';

import richTextParser from 'utils/richTextParser';

// Types
import type { ContentfulTemplatePodcast } from 'graphqlTypes';
import type { FC } from 'react';

interface BasicCardProps extends FlexProps, ContentfulTemplatePodcast {
  backgroundIsDark: boolean;
}

const textWrapCSS = `
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const headingWrapCSS = `
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BasicCard: FC<BasicCardProps> = ({ featuredImage, title, slug, excerpt, backgroundIsDark, ...props }) => (
  <Link href={`/resources/podcast/${slug}`} textDecoration="none">
    <Flex
      width="100%"
      height="100%"
      flexDirection="column"
      borderRadius="12px"
      overflowX="hidden"
      overflowY="hidden"
      hover={{ boxShadow: 'xxl' }}
      transition="200ms ease-in-out"
      backgroundColor={backgroundIsDark ? 'gray-900' : 'common-white'}
      {...props}
    >
      {featuredImage ? (
        <Image
          image={featuredImage as ImageData}
          alt={featuredImage?.title || 'Report Card Image'}
          objectFit="cover"
          objectPosition="left"
          minHeight="148px"
          maxHeight="148px"
          minWidth="100%"
          maxWidth="100%"
          borderRadius="12px"
          sm={{ maxHeight: '200px', minHeight: '200px', minWidth: '50%', maxWidth: '50%' }}
          md={{ maxHeight: '168px', minHeight: '168px', minWidth: '337px', maxWidth: '337px' }}
          lg={{ maxHeight: '195px', minHeight: '195px', minWidth: '390px', maxWidth: '337px' }}
        />
      ) : (
        <Image
          image={{ file: { url: defaultImage } }}
          alt="Report Card Image"
          objectFit="cover"
          objectPosition="center"
          minHeight="148px"
          maxHeight="148px"
          width="100%"
          borderRadius="12px"
          sm={{ maxHeight: '168px', minHeight: '168px', width: '337px' }}
          lg={{ maxHeight: '195px', minHeight: '195px', width: '390px' }}
        />
      )}
      <Flex
        position="relative"
        flexDirection="column"
        justifyContent="space-between"
        gap="16px"
        paddingAll={16}
        height="100%"
      >
        <Flex flexDirection="column" gap="16px">
          <Flex
            paddingX={12}
            paddingY={4}
            borderRadius="16px"
            backgroundColor="navy-50"
            fontSize="textXs"
            lineHeight="textXs"
            textColor="navy-700"
            width="fit-content"
            fontWeight="semiBold"
          >
            Podcast
          </Flex>
          {title && (
            <Heading
              as="h4"
              textStyle="h5"
              textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
              css={headingWrapCSS}
            >
              {title}
            </Heading>
          )}
          {excerpt ? (
            <Text textStyle="textSm" textColor={backgroundIsDark ? 'gray-200' : 'gray-700'} css={textWrapCSS}>
              {richTextParser(excerpt)}
            </Text>
          ) : (
            <Text
              textStyle="textSm"
              textColor={backgroundIsDark ? 'gray-200' : 'gray-900'}
              css={textWrapCSS}
              sm={{ textStyle: 'textMd' }}
            >
              Your business credit score is an essential figure to estimate the creditworthiness of your business. Let
              us walk you through the credit scoring process.
            </Text>
          )}
        </Flex>
        <Flex gap="8px" alignItems="center">
          <FaPlay size={12} color={color.gray[900]} />
          <Flex
            width="100%"
            css={`
              .MuiButton-root {
                width: fit-content !important;
              }
            `}
          >
            <ComponentButton label="Play This Episode" buttonColor="Gray 900 Link" isButton />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  </Link>
);

export default BasicCard;
